import * as XLSX from "xlsx"
import { parseCsvLine } from "./payoff_matrix_csv"

export type TableCell = number | null

export interface TableSpreadsheetData {
  rows: string[]
  columns: string[]
  cells: TableCell[][]
}

function escapeCsvField(value: string): string {
  if (value.includes(",") || value.includes("\n") || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function parseCsvRows(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim()
  if (!normalized) return []
  return normalized.split("\n").map(line => parseCsvLine(line))
}

function parseCell(raw: string): TableCell {
  const trimmed = raw.trim()
  if (trimmed === "") return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}

/**
 * Spreadsheet layout (CSV / Excel):
 * - Row 1: empty corner cell, then column header labels
 * - Following rows: row label, then numeric cell values
 */
export function parseTableSpreadsheetRows(grid: string[][]): TableSpreadsheetData | null {
  if (grid.length < 1) return null

  const header = grid[0].map(cell => String(cell ?? "").trim())
  if (header.length < 2) return null

  const columns = header[0] === "" ? header.slice(1) : header.slice(1)
  if (columns.length === 0) return null

  const dataStartIndex = 1
  const rows: string[] = []
  const cells: TableCell[][] = []

  for (let r = dataStartIndex; r < grid.length; r++) {
    const line = grid[r].map(cell => String(cell ?? ""))
    if (line.every(cell => cell.trim() === "")) continue
    rows.push(line[0]?.trim() || `Row ${r}`)
    cells.push(columns.map((_c, c) => parseCell(line[c + 1] ?? "")))
  }

  return { rows, columns, cells }
}

export function parseTableSpreadsheetCsv(csvText: string): TableSpreadsheetData | null {
  return parseTableSpreadsheetRows(parseCsvRows(csvText))
}

export function parseTableSpreadsheetXlsx(buffer: ArrayBuffer): TableSpreadsheetData | null {
  const workbook = XLSX.read(buffer, { type: "array" })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) return null
  const sheet = workbook.Sheets[sheetName]
  const grid = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" }) as unknown[][]
  const normalized = grid.map(row =>
    (Array.isArray(row) ? row : []).map(cell => String(cell ?? ""))
  )
  return parseTableSpreadsheetRows(normalized)
}

export function buildTableSpreadsheetGrid(
  rows: string[],
  columns: string[],
  cells: TableCell[][]
): string[][] {
  const header = ["", ...columns]
  const body = rows.map((rowLabel, r) => [
    rowLabel,
    ...columns.map((_c, c) => {
      const value = cells[r] ? cells[r][c] : null
      return typeof value === "number" && Number.isFinite(value) ? String(value) : ""
    }),
  ])
  return [header, ...body]
}

export function buildTableSpreadsheetCsv(
  rows: string[],
  columns: string[],
  cells: TableCell[][]
): string {
  const grid = buildTableSpreadsheetGrid(rows, columns, cells)
  return grid
    .map(line => line.map(cell => escapeCsvField(cell)).join(","))
    .join("\n")
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

export function downloadTableSpreadsheetCsv(
  rows: string[],
  columns: string[],
  cells: TableCell[][],
  filename: string
): void {
  const csvContent = buildTableSpreadsheetCsv(rows, columns, cells)
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" })
  triggerDownload(blob, filename.endsWith(".csv") ? filename : `${filename}.csv`)
}

export function downloadTableSpreadsheetXlsx(
  rows: string[],
  columns: string[],
  cells: TableCell[][],
  filename: string
): void {
  const grid = buildTableSpreadsheetGrid(rows, columns, cells)
  const sheet = XLSX.utils.aoa_to_sheet(grid)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, "Table")
  const xlsxData = XLSX.write(workbook, { bookType: "xlsx", type: "array" })
  const blob = new Blob([xlsxData], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  })
  triggerDownload(blob, filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`)
}

/**
 * Merge imported spreadsheet data into the current table. When `lockedColumns`
 * is set, column headers are preserved and values are mapped by column name.
 */
export function mergeImportedTable(
  imported: TableSpreadsheetData,
  lockedColumns: string[] | null
): TableSpreadsheetData {
  if (!lockedColumns || lockedColumns.length === 0) {
    return imported
  }

  const importedColIndex = new Map(imported.columns.map((name, i) => [name, i]))
  const cells = imported.rows.map((_rowLabel, r) =>
    lockedColumns.map(colName => {
      const c = importedColIndex.get(colName)
      if (c === undefined) return null
      return imported.cells[r] ? imported.cells[r][c] : null
    })
  )

  return {
    rows: imported.rows,
    columns: [...lockedColumns],
    cells,
  }
}
